/**
 * S9b — single truth for a direct-mesh entity's on-screen visibility, which is
 * the AND of two independent hide sources:
 *
 *   - **layer visibility** — the entity's layer is turned off / frozen
 *     (`applyAndMirrorLayerVisibility`, driven by the layer table).
 *   - **LOD viewport-cull** — the per-frame cull dropped the entity's line
 *     geometry out of the drawn LOD buckets (`FrameLoops`, driven by camera).
 *
 * A direct mesh (arrow decorator / synthesised text / hatch-wipeout fill) is
 * NOT part of the batched-line buckets, so neither source alone can be a raw
 * `.visible =` write — the last writer would clobber the other. Both concerns
 * therefore record their state (`render.entityLayer` + a hidden-layer set;
 * `render.lodHidden`) and call THIS helper, which recomputes the composite.
 *
 * Codex S9b P2 ("Remove direct MLEADER meshes when culling"): before this, the
 * cull removed only bucket line slots, leaving an MLEADER's arrow + label
 * meshes painted after its leader scrolled into the sub-pixel LOD bucket.
 */

import { isHiddenByLayerChain } from "../layerChainVisibility";
import type { EntityRender } from "./SceneRefsTypes";

/**
 * Re-apply the composite (layer AND lod) visibility to every direct mesh a
 * single `EntityRender` owns. Idempotent — safe to call from either the layer
 * effect or the per-frame cull whenever either input changes.
 *
 * `hiddenLayers` is the current turned-off / frozen layer set. The entity is
 * layer-hidden when its `entityLayer` OR any enclosing block-reference layer
 * in `parentLayers` is hidden (Codex PR #784 round 3 P1 — AutoCAD hides the
 * whole reference with the INSERT's layer). An entry with neither field is
 * treated as layer-visible (only its `lodHidden` gates it).
 */
export function applyEntityRenderVisibility(
  render: EntityRender,
  hiddenLayers: ReadonlySet<string>,
): void {
  const layerHidden = isHiddenByLayerChain(
    render.entityLayer,
    render.parentLayers,
    hiddenLayers,
  );
  const visible = !layerHidden && render.lodHidden !== true;
  if (render.arrowDecoratorMeshes) {
    for (const m of render.arrowDecoratorMeshes) m.visible = visible;
  }
  if (render.textObject) render.textObject.visible = visible;
  if (render.fillMesh) render.fillMesh.visible = visible;
  // Codex PR #784 round-10 P2 ("Cull relifted array meshes through their parent
  // entry") — an `array-rect`'s InstancedMeshes hang off the ARRAY's own render
  // entry, so the cull / layer effect reach them through the same composite as
  // every other direct mesh. Before this they were registered under per-mesh
  // composite keys no cull or layer pass ever addressed, and a culled array kept
  // painting after its spatial entity was removed.
  if (render.arrayInstancedMeshes) {
    for (const m of render.arrayInstancedMeshes) m.visible = visible;
  }
  // S9b (Codex P2 — "Hide synthetic labels when culling parents") — synthesised
  // DIMENSION / MULTILEADER label meshes ride on the PARENT render so the cull +
  // layer effect hide/show them together with the leader's line + arrows.
  if (render.satelliteTextObjects) {
    for (const m of render.satelliteTextObjects) m.visible = visible;
  }
}
